import { useCallback, useMemo, useReducer } from 'react'

import type { SceneryItem } from '@/api/stages'

/**
 * The unsaved scenery layout of one background, with undo. `items === null` means "nothing
 * changed": the editor shows what the server has (the saved layout, or the automatic example).
 * The first edit copies what is shown into the draft. A drag is ONE undo step: `begin` takes a
 * snapshot, `live` moves without recording, `end` records the snapshot if anything moved.
 *
 * The draft lives in the background's page (not the Scenery tab), so switching tabs or pressing
 * "Try it" keeps — and plays — unsaved work.
 */

const HISTORY = 50

interface DraftState {
  items: SceneryItem[] | null
  past: (SceneryItem[] | null)[]
  /** The draft before the gesture in progress; `undefined` when none is. */
  snap: SceneryItem[] | null | undefined
}

type DraftAction =
  | { t: 'set'; items: SceneryItem[] }
  | { t: 'begin' }
  | { t: 'live'; items: SceneryItem[] }
  | { t: 'end' }
  | { t: 'undo' }
  | { t: 'discard' }

const INITIAL: DraftState = { items: null, past: [], snap: undefined }

function push(past: DraftState['past'], prev: SceneryItem[] | null): DraftState['past'] {
  return [...past.slice(-(HISTORY - 1)), prev]
}

export function draftReducer(s: DraftState, a: DraftAction): DraftState {
  switch (a.t) {
    case 'set':
      return { items: a.items, past: push(s.past, s.items), snap: undefined }
    case 'begin':
      return { ...s, snap: s.items }
    case 'live':
      return { ...s, items: a.items }
    case 'end': {
      if (s.snap === undefined) return s
      const changed = s.items !== s.snap
      return { items: s.items, past: changed ? push(s.past, s.snap) : s.past, snap: undefined }
    }
    case 'undo': {
      if (s.past.length === 0) return s
      return { items: s.past[s.past.length - 1] ?? null, past: s.past.slice(0, -1), snap: undefined }
    }
    case 'discard':
      return INITIAL
  }
}

export interface SceneryDraft {
  /** The unsaved layout, or null while nothing has changed. */
  items: SceneryItem[] | null
  canUndo: boolean
  /** Replace the layout (one undo step). */
  set(items: SceneryItem[]): void
  begin(): void
  live(items: SceneryItem[]): void
  end(): void
  undo(): void
  /** Forget the draft (after a save, or "Use theirs"). */
  discard(): void
}

export function useSceneryDraft(): SceneryDraft {
  const [state, dispatch] = useReducer(draftReducer, INITIAL)
  const set = useCallback((items: SceneryItem[]) => dispatch({ t: 'set', items }), [])
  const begin = useCallback(() => dispatch({ t: 'begin' }), [])
  const live = useCallback((items: SceneryItem[]) => dispatch({ t: 'live', items }), [])
  const end = useCallback(() => dispatch({ t: 'end' }), [])
  const undo = useCallback(() => dispatch({ t: 'undo' }), [])
  const discard = useCallback(() => dispatch({ t: 'discard' }), [])
  return useMemo(
    () => ({ items: state.items, canUndo: state.past.length > 0, set, begin, live, end, undo, discard }),
    [state.items, state.past.length, set, begin, live, end, undo, discard],
  )
}
